import toast, { Toast } from "react-hot-toast";
import { createElement, type CSSProperties, type ReactElement } from "react";

export enum TOAST_TYPES {
  info,
  success,
  error,
  warning,
}

export const showDynamicToast = (type: TOAST_TYPES, title: string, message: string) => {
  // Create a custom toast with different padding for dynamic toast
  const options: Partial<Pick<Toast, "duration" | "position">> = {
    duration: 5000,
    position: "bottom-right" as const,
  };

  // Dynamic toast specific style with smaller padding
  const dynamicToastStyle: CSSProperties = {
    borderRadius: "8px",
    padding: "8px 24px", // Different padding for dynamic toast
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    background: "#fff",
    color: "#111",
    fontSize: "14px",
    display: "flex",
    alignItems: "start",
    gap: "8px",
    position: "relative",
    minWidth: "375px",
    justifyContent: "start",
  };

  const closeButtonStyle: CSSProperties = {
    position: "absolute",
    top: 10,
    right: 6,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 6,
    lineHeight: 1,
    borderRadius: 6,
    color: "#6b7280",
    fontSize: "20px",
    fontWeight: "semibold",
  };

  const getClassName = (toastType: TOAST_TYPES) => {
    switch (toastType) {
      case TOAST_TYPES.info:
        return "toast-with-after info";
      case TOAST_TYPES.error:
        return "toast-with-after error";
      case TOAST_TYPES.success:
        return "toast-with-after success";
      case TOAST_TYPES.warning:
        return "toast-with-after warning";
      default:
        return "toast-with-after info";
    }
  };

  const render = (t: Toast, toastType: TOAST_TYPES, content: string | ReactElement) => {
    const children: Array<ReactElement | string> = [];

    if (toastType === TOAST_TYPES.warning) {
      children.push(createElement("span", { key: "icon", "aria-hidden": "true" }, "⚠️"));
    }

    children.push(content as any);

    children.push(
      createElement(
        "button",
        {
          key: "close",
          type: "button",
          "aria-label": "Dismiss",
          "data-testid": "toast-close-button",
          style: closeButtonStyle,
          onClick: (event) => {
            const target = event?.currentTarget as HTMLElement | undefined;
            const root = target?.closest('[data-testid="toast-root"]') as HTMLElement | null;
            if (root) {
              root.style.transition = "transform 250ms ease, opacity 250ms ease";
              root.style.transform = "translateX(120%)";
              root.style.opacity = "0";
            }
            setTimeout(() => toast.dismiss(t.id), 250);
          },
        },
        "×",
      )
    );

    const isVisible = (t as unknown as { visible?: boolean }).visible !== false;
    const containerStyle: CSSProperties = {
      ...dynamicToastStyle,
      transition: "transform 250ms ease, opacity 250ms ease",
      transform: isVisible ? "translateX(0)" : "translateX(120%)",
      opacity: isVisible ? 1 : 0,
      willChange: "transform, opacity",
    };

    return createElement(
      "div",
      {
        className: getClassName(type),
        style: containerStyle,
        "data-testid": "toast-root",
      },
      ...children,
    );
  };

  toast.custom((t) => render(t, type, createElement(
    'div',
    null,
    createElement('span', { className: "text-sm font-medium" }, title),
    createElement('br'),
    createElement('span', { className: "text-sm text-[#a0a7b0] font-medium" }, message)
  )), { ...options });
};

export const showToast = (type: TOAST_TYPES, data: string | ReactElement | null, delay?: number) => {
  // Do not auto-dismiss existing toasts; prevents instant dismissal when multiple calls happen close together

  const options: Partial<Pick<Toast, "duration" | "position">> = {
    duration: 5000,
    position: "bottom-right" as const,
  };

  const baseStyle: CSSProperties = {
    borderRadius: "8px",
    padding: "16px 28px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    background: "#fff",
    color: "#111",
    fontSize: "14px",
    display: "flex",
    alignItems: "start",
    gap: "8px",
    position: "relative",
    minWidth: "375px",
    justifyContent: "start",
  };

  const closeButtonStyle: CSSProperties = {
    position: "absolute",
    top: 10,
    right: 6,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 6,
    lineHeight: 1,
    borderRadius: 6,
    color: "#6b7280",
    fontSize: "20px",
    fontWeight: "semibold",
  };

  const getClassName = (toastType: TOAST_TYPES) => {
    switch (toastType) {
      case TOAST_TYPES.info:
        return "toast-with-after info";
      case TOAST_TYPES.error:
        return "toast-with-after error";
      case TOAST_TYPES.success:
        return "toast-with-after success";
      case TOAST_TYPES.warning:
        return "toast-with-after warning";
      default:
        return "toast-with-after info";
    }
  };

  const resolveMessage = (toastType: TOAST_TYPES, value: string | ReactElement | null): string | ReactElement => {
    if (toastType === TOAST_TYPES.error) {
      if (typeof value === "string") {
        return value || "Something Went Wrong";
      }
      return "Something Went Wrong";
    }
    return value ?? "";
  };

  const render = (t: Toast, toastType: TOAST_TYPES, content: string | ReactElement) => {
    const children: Array<ReactElement | string> = [];

    if (toastType === TOAST_TYPES.warning) {
      children.push(createElement("span", { key: "icon", "aria-hidden": "true" }, "⚠️"));
    }

    children.push(content as any);

    children.push(
      createElement(
        "button",
        {
          key: "close",
          type: "button",
          "aria-label": "Dismiss",
          "data-testid": "toast-close-button",
          style: closeButtonStyle,
          onClick: (event) => {
            const target = event?.currentTarget as HTMLElement | undefined;
            const root = target?.closest('[data-testid="toast-root"]') as HTMLElement | null;
            if (root) {
              root.style.transition = "transform 250ms ease, opacity 250ms ease";
              root.style.transform = "translateX(120%)";
              root.style.opacity = "0";
            }
            setTimeout(() => toast.dismiss(t.id), 250);
          },
        },
        "×",
      )
    );

    // Animate enter/exit: slide to right and fade on dismiss
    const isVisible = (t as unknown as { visible?: boolean }).visible !== false;
    const containerStyle: CSSProperties = {
      ...baseStyle,
      transition: "transform 250ms ease, opacity 250ms ease",
      transform: isVisible ? "translateX(0)" : "translateX(120%)",
      opacity: isVisible ? 1 : 0,
      willChange: "transform, opacity",
    };

    return createElement(
      "div",
      {
        className: getClassName(toastType),
        style: containerStyle,
        "data-testid": "toast-root",
      },
      ...children,
    );
  };

  switch (type) {
    case TOAST_TYPES.info:
      toast.custom((t) => render(t, type, resolveMessage(type, data)), { ...options });
      break;
    case TOAST_TYPES.error:
      toast.custom((t) => render(t, type, resolveMessage(type, data)), { ...options });
      break;
    case TOAST_TYPES.success:
      toast.custom((t) => render(t, type, resolveMessage(type, data)), { ...options });
      break;
    case TOAST_TYPES.warning:
      toast.custom((t) => render(t, type, resolveMessage(type, data)), { ...options });
      break;
    default:
      toast.custom((t) => render(t, type, resolveMessage(type, data)), { ...options });
  }
};


